/** @jsxImportSource ../../i18n */
import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cx } from '../../theme/cx';
import { IconTile } from './Card';

export const Spinner = ({ size = 16, label, tone = 'gold', className, labelClassName }) => (
  <span role={label ? 'status' : undefined} className={cx('inline-flex items-center gap-2', className)}>
    <span
      aria-hidden="true"
      className={cx(
        'inline-block flex-shrink-0 animate-spin rounded-full border-2',
        tone === 'current' ? 'border-current border-r-transparent opacity-80' : 'border-gold-500/25 border-t-gold-500',
      )}
      style={{ width: size, height: size }}
    />
    {label && <span className={cx('text-sm text-fg-muted', labelClassName)}>{label}</span>}
  </span>
);

export const Progress = ({ value = 0, max = 100, className, label }) => {
  const ratio = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;
  return (
    <div className={cx('h-1.5 overflow-hidden rounded-full bg-surface-3', className)} role={label ? 'progressbar' : undefined} aria-label={label} aria-valuenow={label ? Math.round(ratio * 100) : undefined} aria-valuemin={label ? 0 : undefined} aria-valuemax={label ? 100 : undefined}>
      <div className="h-full origin-left rounded-full bg-gold-500 transition-transform duration-500 ease-standard" style={{ transform: `scaleX(${ratio})` }} />
    </div>
  );
};

export const Skeleton = ({ className }) => <span aria-hidden="true" className={cx('skeleton block', className)} />;

export const SkeletonText = ({ lines = 3, className }) => (
  <span aria-hidden="true" className={cx('block space-y-2.5', className)}>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton key={i} className={cx('h-3', i === lines - 1 ? 'w-2/3' : 'w-full')} />
    ))}
  </span>
);

export const SkeletonStat = ({ className }) => (
  <div aria-hidden="true" className={cx('rounded-card border border-line bg-surface-1 p-5 sm:p-6', className)}>
    <Skeleton className="h-3 w-20" />
    <Skeleton className="mt-4 h-7 w-32" />
    <Skeleton className="mt-3 h-3 w-16" />
  </div>
);

export const SkeletonTable = ({ rows = 5, cols = 5, className }) => (
  <div aria-hidden="true" className={cx('space-y-4', className)}>
    <div className="flex gap-6 border-b border-line pb-3">
      {Array.from({ length: cols }, (_, i) => <Skeleton key={i} className={cx('h-2.5', i === 0 ? 'w-28' : 'ml-auto w-16')} />)}
    </div>
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex items-center gap-6">
        <Skeleton className="h-9 w-9 rounded-control" />
        <Skeleton className="h-3 w-32" />
        {Array.from({ length: cols - 2 }, (_, c) => <Skeleton key={c} className="ml-auto h-3 w-16" />)}
      </div>
    ))}
  </div>
);

export const SkeletonList = ({ items = 4, className }) => (
  <div aria-hidden="true" className={cx('space-y-3', className)}>
    {Array.from({ length: items }, (_, i) => (
      <div key={i} className="rounded-control border border-line bg-surface-2/50 p-4">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-3.5 w-3/4" />
        <Skeleton className="mt-2 h-3 w-1/2" />
      </div>
    ))}
  </div>
);

export const EmptyState = ({ icon, title, description, action, tone = 'neutral', className, children }) => (
  <div className={cx('flex flex-col items-center px-6 py-12 text-center', className)}>
    {icon && <IconTile icon={icon} tone={tone} size="lg" />}
    {title && <h3 className={cx('font-display text-xl text-fg', icon && 'mt-5')}>{title}</h3>}
    {description && <p className="mt-2 max-w-md text-sm leading-relaxed text-fg-muted">{description}</p>}
    {children}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

const BANNER_TONES = {
  info: ['border-info-500/20 bg-info-900/40 text-info-200', Info, 'text-info-400'],
  success: ['border-gain-500/20 bg-gain-900/40 text-gain-200', CheckCircle2, 'text-gain-400'],
  warn: ['border-warn-500/25 bg-warn-900/40 text-warn-200', AlertTriangle, 'text-warn-400'],
  loss: ['border-loss-500/25 bg-loss-900/40 text-loss-200', AlertCircle, 'text-loss-400'],
  gold: ['border-gold-500/25 bg-gold-900/40 text-gold-200', Info, 'text-gold-400'],
  neutral: ['border-line bg-surface-2 text-fg-body', Info, 'text-fg-muted'],
};

export const Banner = ({ tone = 'info', icon, title, children, action, role, compact = false, className, ...rest }) => {
  const [toneClass, DefaultIcon, iconClass] = BANNER_TONES[tone] || BANNER_TONES.info;
  const Icon = icon === null ? null : icon || DefaultIcon;
  return (
    <div role={role} className={cx('flex items-start gap-3 rounded-control border text-sm', compact ? 'px-3 py-2' : 'px-4 py-3', toneClass, className)} {...rest}>
      {Icon && <Icon size={compact ? 15 : 17} className={cx('mt-0.5 flex-shrink-0', iconClass)} aria-hidden="true" />}
      <div className="min-w-0 flex-1 leading-relaxed">
        {title && <p className="font-semibold text-fg">{title}</p>}
        {children && <div className={cx('break-words', title && 'mt-0.5')}>{children}</div>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
};
